import { RefObject, useEffect } from 'react';

const EDGE_EPSILON = 1;

function isAtTop(el: HTMLElement) {
  return el.scrollTop <= EDGE_EPSILON;
}

function isAtBottom(el: HTMLElement) {
  return el.scrollTop + el.clientHeight >= el.scrollHeight - EDGE_EPSILON;
}

function hasOverflow(el: HTMLElement) {
  return el.scrollHeight > el.clientHeight + EDGE_EPSILON;
}

export function useVerticalScrollChaining(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (!hasOverflow(el)) return;

      if (isAtTop(el) && e.deltaY < 0) {
        window.scrollBy({ top: e.deltaY });
        return;
      }
      if (isAtBottom(el) && e.deltaY > 0) {
        window.scrollBy({ top: e.deltaY });
      }
    };

    let lastTouchY = 0;

    const onTouchStart = (e: TouchEvent) => {
      lastTouchY = e.touches[0]?.clientY ?? 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;

      const deltaY = lastTouchY - touch.clientY;
      lastTouchY = touch.clientY;

      if (!hasOverflow(el)) {
        window.scrollBy({ top: deltaY });
        return;
      }

      if (isAtTop(el) && deltaY < 0) {
        window.scrollBy({ top: deltaY });
        return;
      }
      if (isAtBottom(el) && deltaY > 0) {
        window.scrollBy({ top: deltaY });
      }
    };

    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
    };
  }, [ref]);
}
